import { useState, useEffect, useRef } from 'react'
import { getAccessToken, opcoHeaders } from '../services/api'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

// Module-level cache to avoid duplicate photo fetches across component instances
// userId -> { url: blobUrl | null, ts: timestamp }
const photoCache = new Map()

// Photo fetching requires User.Read.All (Application) permission in Entra
const PHOTO_FETCHING_ENABLED = true

// Don't retry failed fetches for 60 seconds (avoids hammering the API)
const RETRY_DELAY_MS = 60000

function fetchUserPhoto(userId) {
  if (!PHOTO_FETCHING_ENABLED) return Promise.resolve(null)

  if (photoCache.has(userId)) {
    const cached = photoCache.get(userId)
    if (cached === 'pending') return new Promise(resolve => {
      const check = setInterval(() => {
        const val = photoCache.get(userId)
        if (val !== 'pending') { clearInterval(check); resolve(val?.url ?? null) }
      }, 50)
    })
    // If we have a successful URL, return it
    if (cached.url) return Promise.resolve(cached.url)
    // If it failed recently, don't retry yet
    if (Date.now() - cached.ts < RETRY_DELAY_MS) return Promise.resolve(null)
    // Otherwise, retry (fall through)
  }

  photoCache.set(userId, 'pending')

  return (async () => {
    try {
      const token = getAccessToken()
      if (!token) {
        // Token not ready — don't cache, allow immediate retry on next render
        photoCache.delete(userId)
        return null
      }

      const resp = await fetch(`${API_BASE}/admin/users/${userId}/photo`, {
        headers: { 'Authorization': `Bearer ${token}`, ...opcoHeaders() },
      })

      if (!resp.ok) {
        // 404 is expected when user has no profile photo set — don't log it
        if (resp.status !== 404) {
          console.warn(`[UserAvatar] Photo fetch failed for ${userId}: HTTP ${resp.status}`)
        }
        photoCache.set(userId, { url: null, ts: Date.now() })
        return null
      }

      const blob = await resp.blob()
      if (!blob || blob.size === 0) {
        photoCache.set(userId, { url: null, ts: Date.now() })
        return null
      }

      const url = URL.createObjectURL(blob)
      photoCache.set(userId, { url, ts: Date.now() })
      return url
    } catch (err) {
      console.warn(`[UserAvatar] Photo fetch error for ${userId}:`, err)
      photoCache.set(userId, { url: null, ts: Date.now() })
      return null
    }
  })()
}

function fetchEntraUserPhoto(entraUserId) {
  if (!PHOTO_FETCHING_ENABLED) return Promise.resolve(null)

  const cacheKey = `entra:${entraUserId}`
  if (photoCache.has(cacheKey)) {
    const cached = photoCache.get(cacheKey)
    if (cached === 'pending') return new Promise(resolve => {
      const check = setInterval(() => {
        const val = photoCache.get(cacheKey)
        if (val !== 'pending') { clearInterval(check); resolve(val?.url ?? null) }
      }, 50)
    })
    if (cached.url) return Promise.resolve(cached.url)
    if (Date.now() - cached.ts < RETRY_DELAY_MS) return Promise.resolve(null)
  }

  photoCache.set(cacheKey, 'pending')

  return (async () => {
    try {
      const token = getAccessToken()
      if (!token) {
        photoCache.delete(cacheKey)
        return null
      }

      const resp = await fetch(`${API_BASE}/admin/entra/users/${entraUserId}/photo`, {
        headers: { 'Authorization': `Bearer ${token}`, ...opcoHeaders() },
      })

      if (!resp.ok) {
        if (resp.status !== 404) {
          console.warn(`[UserAvatar] Entra photo fetch failed for ${entraUserId}: HTTP ${resp.status}`)
        }
        photoCache.set(cacheKey, { url: null, ts: Date.now() })
        return null
      }

      const blob = await resp.blob()
      if (!blob || blob.size === 0) {
        photoCache.set(cacheKey, { url: null, ts: Date.now() })
        return null
      }

      const url = URL.createObjectURL(blob)
      photoCache.set(cacheKey, { url, ts: Date.now() })
      return url
    } catch (err) {
      console.warn(`[UserAvatar] Entra photo fetch error for ${entraUserId}:`, err)
      photoCache.set(cacheKey, { url: null, ts: Date.now() })
      return null
    }
  })()
}

/**
 * Reusable user avatar with photo support.
 * Fetches the user's profile photo from the backend proxy with auth token.
 * Falls back to initials on error or if no photo is available.
 */
export default function UserAvatar({ user, size = 'md', className = '' }) {
  const photoMode = user?.photo_mode || 'xtract'
  const userId = user?.id
  const photoId = user?.photo_id || userId
  const cacheKey = photoMode === 'entra' ? `entra:${photoId}` : photoId

  const [blobUrl, setBlobUrl] = useState(() => {
    // Use cached value synchronously if available
    const cached = photoCache.get(cacheKey)
    return (cached && cached !== 'pending' && cached.url) ? cached.url : null
  })
  const mountedRef = useRef(true)

  const isAdmin = user?.role === 'admin'
  const name = user?.username || user?.email || '?'
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || name[0].toUpperCase()
  useEffect(() => {
    const cached = photoCache.get(cacheKey)
    setBlobUrl((cached && cached !== 'pending' && cached.url) ? cached.url : null)
  }, [cacheKey])

  useEffect(() => {
    mountedRef.current = true
    if (!photoId) return

    let retryTimer = null
    let retries = 0
    const MAX_RETRIES = 5

    const attemptFetch = () => {
      const fetcher = photoMode === 'entra' ? fetchEntraUserPhoto : fetchUserPhoto
      fetcher(photoId).then(url => {
        if (!mountedRef.current) return
        if (url) {
          setBlobUrl(url)
        } else if (!photoCache.has(cacheKey) && retries < MAX_RETRIES) {
          // Cache was deleted (token wasn't ready) — retry after 1s
          retries++
          retryTimer = setTimeout(attemptFetch, 1000)
        }
      })
    }

    attemptFetch()

    return () => {
      mountedRef.current = false
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [photoId, photoMode, cacheKey])

  const sizes = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
  }

  const colors = isAdmin
    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'

  const sizeClass = sizes[size] || sizes.md

  if (blobUrl) {
    return (
      <img
        src={blobUrl}
        alt={user?.username || 'User'}
        onError={() => setBlobUrl(null)}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0 ${className}`}
      />
    )
  }

  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${colors} ${className}`}>
      {initials}
    </div>
  )
}
